import {Card, DataStatus, Table, TableRow} from 'platform/components';
import {Box, Link, Show, Text, VStack} from 'platform/foundation';

import {head, isNotNil, sort} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  AdvertisementStatusEnum,
  useGetGeneralSettingsQuery,
  useGetVehicleQuery,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {EMPTY_PLACEHOLDER, fakePlatforms, platformImageURLs, usePermissions} from '@dms/shared';

import {getSafely, parseDate} from 'shared/web';

import {useAdvertisementPlatformFilter} from '../hooks/useAdvertisementPlatformFilter';
import {AdditionalInfoStatus} from './AdditionalInfoStatus';
import {PlatformActions} from './PlatformActions';
import {PlatformDate} from './PlatformDate';
import {PlatformLogo} from './PlatformLogo';
import {PlatformPrice} from './PlatformPrice';
import {PlatformSwitcher} from './PlatformSwitcher';
import {PlatformUpdatedBy} from './PlatformUpdatedBy';
import {StatusItem} from './StatusItem';

const EMPTY_COLUMN_HEADER = null;

const COLUMN_TITLES = [
  EMPTY_COLUMN_HEADER,
  i18n.t('page.advertisement.labels.platformName'),
  i18n.t('page.advertisement.labels.status'),
  i18n.t('page.advertisement.labels.url'),
  i18n.t('page.advertisement.labels.advertisedPrice'),
  i18n.t('page.advertisement.labels.data'),
  i18n.t('page.advertisement.labels.publishingBranch'),
  i18n.t('page.advertisement.labels.updatedBy'),
  i18n.t('page.advertisement.labels.updated'),
  EMPTY_COLUMN_HEADER,
] as const;

export interface AdPlatformProps {
  vehicleId: string;
  isPollingEnabled: boolean;
}

export function AdPlatforms(props: AdPlatformProps) {
  const {data: generalSettings} = useGetGeneralSettingsQuery({vehicleId: props.vehicleId});
  const inSale = !!generalSettings?.inSale;

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });

  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});

  const [hasEnablePlatformForVehiclePermission, hasDisablePlatformForVehiclePermission] =
    usePermissions({
      permissionKeys: ['enablePlatformForVehicle', 'disablePlatformForVehicle'],
      scopes: {
        enablePlatformForVehicle: {
          participation: vehicleParticipation,
          branchId: vehicle?.branchId,
        },
        disablePlatformForVehicle: {
          participation: vehicleParticipation,
          branchId: vehicle?.branchId,
        },
      },
    });

  const isDisabled =
    !hasEnablePlatformForVehiclePermission || !hasDisablePlatformForVehiclePermission;

  const {
    platforms: filteredPlatforms,
    isLoading,
    isError,
    allPlatforms,
  } = useAdvertisementPlatformFilter({
    vehicleId: props.vehicleId,
    skipPolling: !props.isPollingEnabled,
  });

  const triggeredUpdates = allPlatforms?.map((platform) => platform.statusTriggeredAt) ?? [];
  const latestUpdate =
    head(
      sort(
        (a, b) => (parseDate(b)?.getTime() ?? 0) - (parseDate(a)?.getTime() ?? 0),
        triggeredUpdates
      )
    ) ?? '';

  const items = filteredPlatforms?.map((platformData) => {
    const code = platformData.platform.code;
    const platformTitle = platformData.platform.title;
    const status = platformData.status;

    return (
      <TableRow key={code} data-testid={testIds.vehicles.publishing(`platform-${code}`)}>
        <Box paddingHorizontal={2}>
          <PlatformSwitcher
            vehicleSaleState={vehicle?.saleState ?? null}
            code={code}
            currentStatus={status}
            inSale={inSale}
            value={platformData.enabled}
            isDisabled={isDisabled}
          />
        </Box>
        <PlatformLogo src={getSafely(platformImageURLs, code)} name={platformData.platform.title} />
        <StatusItem
          currentStatus={platformData.status}
          statusDescription={platformData.statusDescription}
          platformCode={code}
        />
        <div>
          {isNilOrEmpty(platformData.url) ? (
            EMPTY_PLACEHOLDER
          ) : (
            <Link href={platformData.url} target="_blank" size="small">
              {i18n.t('page.advertisement.labels.show_ad')}
            </Link>
          )}
        </div>
        <PlatformPrice price={platformData.priceWithVat} />
        <div>
          {platformData.hasOverrides
            ? i18n.t('general.labels.custom')
            : i18n.t('general.labels.default')}
        </div>
        <div>
          {platformData.status === AdvertisementStatusEnum.NOT_PUBLISHED
            ? EMPTY_PLACEHOLDER
            : (platformData.publishingBranchName ?? i18n.t('entity.tenant.labels.tenant'))}
        </div>
        <PlatformUpdatedBy
          text={
            platformData.statusTriggeredBy ??
            (isNotNil(platformData.statusTriggeredAt)
              ? i18n.t('page.advertisement.labels.systemUser')
              : null)
          }
        />
        <PlatformDate platformCode={code} updatedAt={platformData.statusTriggeredAt} />
        <PlatformActions
          platformCode={code}
          vehicleId={props.vehicleId}
          currentStatus={status}
          platformTitle={platformTitle}
          inSale={inSale}
          statusTriggeredAt={platformData.statusTriggeredAt}
        />
      </TableRow>
    );
  });

  const fakeItems = fakePlatforms.map(({code, title}) => {
    const status = AdvertisementStatusEnum.NOT_PUBLISHED;

    return (
      <TableRow key={code} data-testid={testIds.vehicles.publishing(`platform-${code}`)}>
        <Box paddingHorizontal={2}>
          <PlatformSwitcher
            vehicleSaleState={vehicle?.saleState ?? null}
            // @ts-ignore Fake platform code
            code={code}
            currentStatus={status}
            inSale={inSale}
            value={false}
            isDisabled
          />
        </Box>
        <PlatformLogo src={getSafely(platformImageURLs, code)} name={title} />
        <StatusItem
          currentStatus={AdvertisementStatusEnum.NOT_PUBLISHED}
          statusDescription={null}
          platformCode={code}
        />
        <div>{EMPTY_PLACEHOLDER}</div>
        <PlatformPrice price={null} />
        <div>{i18n.t('general.labels.default')}</div>
        <div>{EMPTY_PLACEHOLDER}</div>
        <PlatformUpdatedBy />
        <PlatformDate platformCode={code} />
        <PlatformActions
          // @ts-ignore Fake platform code
          platformCode={code}
          vehicleId={props.vehicleId}
          currentStatus={status}
          platformTitle={title}
          inSale={inSale}
          statusTriggeredAt={null}
        />
      </TableRow>
    );
  });

  return (
    <Card>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={!items?.length}
        emptyMessage={i18n.t('page.advertisement.labels.notAvailablePlatforms')}
        spacing={10}
      >
        <VStack spacing={4}>
          <AdditionalInfoStatus latestUpdate={latestUpdate} inAdvertisement={inSale} />
          <Box overflowX="auto">
            <Box minWidth={240}>
              <Table
                columns={COLUMN_TITLES.map((item) => ({
                  element: (
                    <Box paddingBottom={2}>
                      <Text key={item} size="xSmall" align="left">
                        {item}
                      </Text>
                    </Box>
                  ),
                }))}
                data-testid={testIds.vehicles.publishing(`platform-list`)}
              >
                {items}
                <Show whenFeatureEnabled={featureFlags.ADVERTISEMENT_FAKE_PLATFORMS}>
                  {fakeItems}
                </Show>
              </Table>
            </Box>
          </Box>
        </VStack>
      </DataStatus>
    </Card>
  );
}
